import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RegistrationModal from "@/components/RegistrationModal";
import BattleRegistrationModal from "@/components/BattleRegistrationModal";
import { api } from "@/lib/api";
import { ArrowUpRight, Timer, Swords, Sparkles, ShieldCheck, Award, Bot, BriefcaseBusiness } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

// Hero banners that auto-rotate every 6s
const HERO_BANNERS = [
  {
    eyebrow: "Kiba Labs · AI Summit Edition",
    title: <>Battle. Win.<br /><span className="proctorly-mark">Proctorly AI.</span></>,
    blurb: "Sixty seconds. One AI-generated question. A score that's actually verifiable. Step on stage, answer the AI, and walk out with a credential that holds up on LinkedIn.",
  },
  {
    eyebrow: "Battle Lane · 01 · Know our Bihar",
    title: <>Aee Bihari,<br />Kya Hai <span className="text-[color:var(--kl-red)]">Bihar?</span></>,
    blurb: "From Nalanda to the modern startup wave — test how well you really know Bihar's history, culture, geography, and rising voices. AI questions, fresh every play.",
  },
  {
    eyebrow: "Battle Lane · 02 · Hindi Cinema",
    title: <>Lights Camera Action <span className="text-[color:var(--kl-red)]">Cinema.</span></>,
    blurb: "Bollywood legends, iconic dialogues, classic films, directors, chart-topping songs — how well do you really know Hindi cinema? Face the AI and find out.",
  },
  {
    eyebrow: "Battle Lane · 03 · Current Affairs",
    title: <>Kya <span className="text-[color:var(--kl-red)]">chal</span><br />raha hai?</>,
    blurb: "World events, history, geography, what made the headlines this morning — answer the AI and prove you read past the hot take.",
  },
  {
    eyebrow: "Battle Lane · 04 · Know Science",
    title: <>Aao <span className="text-[color:var(--kl-red)]">Vigyaan</span><br />sikhe.</>,
    blurb: "Class 7 to 12 — Physics, Chemistry, Biology. Hindi or English. Take a quick science blitz and see if you still remember why the sky is blue.",
  },
];

const BANNER_INTERVAL_MS = 6_000;


export default function Landing() {
  const navigate = useNavigate();
  const [blitzOpen, setBlitzOpen] = useState(false);
  const [battleOpen, setBattleOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [bannerIdx, setBannerIdx] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Rotate hero banners every BANNER_INTERVAL_MS, looping indefinitely.
  useEffect(() => {
    const id = setInterval(() => {
      setBannerIdx((i) => (i + 1) % HERO_BANNERS.length);
    }, BANNER_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Poll for live tournament every 3.5s
  useEffect(() => {
    const tick = () => api.tournamentCurrent().then((r) => setTournament(r.tournament)).catch(() => {});
    tick();
    const id = setInterval(tick, 3500);
    return () => clearInterval(id);
  }, []);

  const [pendingMode, setPendingMode] = useState(null); // "quiz" | "chat"

  const onBlitzDone = (player) => {
    setBlitzOpen(false);
    // Pass player via router state so Blitz.jsx can skip the gate
    const path = pendingMode === "chat" ? "/blitz?mode=chat" : "/blitz";
    navigate(path, { state: { player, verified: true } });
    setPendingMode(null);
  };

  const openBlitz = (mode = null) => {
    // Always open the registration modal — no localStorage check
    setPendingMode(mode);
    setBlitzOpen(true);
  };

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      {/* ===== Hero ===== */}
      <section className="min-h-[100vh] flex flex-col" data-testid="landing-hero" style={{ background: "#000" }}>

        {/* header */}
        <header
          className={`relative z-30 flex items-center justify-between px-6 md:px-12 py-5 transition-all duration-300 ${
            scrolled ? "border-b border-white/10 backdrop-blur-md bg-black/60" : ""
          }`}
          data-testid="site-header"
        >
          <a href="/" className="flex items-center text-white" data-testid="brand-link">
            <div className="leading-tight">
              <div className="font-display text-2xl md:text-3xl tracking-tight text-white">
                PROCTORLY <span className="text-[color:var(--kl-red)]">AI</span>
              </div>
              <div className="font-mono-kl text-[9px] tracking-[0.25em] uppercase text-white/55 mt-0.5">
                Powered by Kiba Labs
              </div>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline-flex pill-tag text-white/80"><span className="dot" />AI Summit · Live Demo</span>
            {/* Quick-links: always visible in the hero bar */}
            <button
              onClick={() => navigate("/leaderboard")}
              className="hidden md:flex items-center gap-1.5 font-mono-kl text-[10px] uppercase tracking-[0.25em] text-[color:var(--kl-yellow)] hover:text-white transition"
            >
              🏆 Leaderboard
            </button>
            <span className="text-white/15 hidden md:block">|</span>
            <button
              onClick={() => navigate("/live")}
              className="hidden md:flex items-center gap-1.5 font-mono-kl text-[10px] uppercase tracking-[0.25em] text-[color:var(--kl-red)] hover:text-white transition"
            >
              <span className="animate-pulse">●</span> Live Arena
            </button>
            <span className="text-white/15 hidden md:block">|</span>
            <button onClick={() => setBlitzOpen(true)} className="btn-submit" data-testid="header-play-btn">
              Play Now <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Split row: fixed height so video panel never resizes with text */}
        <div className="flex" style={{ height: "calc(100vh - 72px)" }}>

          {/* LEFT — black, text content, same padding as before */}
          <div className="w-full md:w-1/2 flex flex-col justify-center px-6 md:px-12 py-10 md:py-16 overflow-hidden">
            <div className="banner-shell" key={`shell-${bannerIdx}`} data-testid="hero-banner">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/60 font-mono-kl" data-testid="hero-eyebrow">
                <span className="kl-accent-line" />
                {HERO_BANNERS[bannerIdx].eyebrow}
              </div>
              <h1
                className="font-display text-white mt-4 leading-[1.05] tracking-tight uppercase text-[14vw] sm:text-[12vw] md:text-[8.5vw] lg:text-[8rem]"
                data-testid="landing-title"
              >
                {HERO_BANNERS[bannerIdx].title}
              </h1>
              <div className="flex items-center gap-3 mt-5" data-testid="powered-by">
                <span className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-white/55">Powered by</span>
                <img src="/kiba-logo.png" alt="Kiba Labs" className="h-10 w-10 object-contain" />
              </div>
              <p className="max-w-2xl mt-7 text-white/75 text-base md:text-xl leading-relaxed" data-testid="hero-blurb">
                {HERO_BANNERS[bannerIdx].blurb}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={() => openBlitz()} className="btn-submit" data-testid="hero-cta-blitz">
                Take the 60-second Blitz <ArrowUpRight className="w-4 h-4" />
              </button>
              <button onClick={() => setBattleOpen(true)} className="btn-ghost-dark" data-testid="hero-cta-battle">
                Or start a 2-player battle
              </button>
            </div>

            <div className="flex gap-1.5 mt-7" data-testid="banner-dots">
              {HERO_BANNERS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setBannerIdx(i)}
                  data-testid={`banner-dot-${i}`}
                  className={`h-1.5 transition-all ${i === bannerIdx ? "w-10 bg-[color:var(--kl-red)]" : "w-4 bg-white/25 hover:bg-white/50"}`}
                  aria-label={`Banner ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — current-affairs.mp4, strictly contained, hidden on mobile */}
          <div className="hidden md:block md:w-1/2 relative overflow-hidden h-full" style={{ background: "#000" }}>
            {/* permanent black base so there's never a white flash while video loads/loops */}
            <div className="absolute inset-0" style={{ background: "#000" }} />
            <video
              autoPlay muted loop playsInline preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-video"
              style={{ background: "#000" }}
            >
              <source src="/hero-1.mp4" type="video/mp4" />
            </video>
            {/* left edge fade to blend into black */}
            <div className="absolute inset-y-0 left-0 w-24 z-10 pointer-events-none"
              style={{ background: "linear-gradient(to right, #000, transparent)" }} />
          </div>
        </div>

        {/* Feature cards — solid black, no video behind them */}
        <div className="px-6 md:px-12 pb-12 md:pb-16" style={{ background: "#000" }}>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-7">
            <FeatureCard
              n="01"
              kicker="Chat · AI Companion"
              title={<>Talk to<br /><span className="text-[color:var(--kl-red)]">KIBA.</span></>}
              desc="Have a real conversation with Kiba — your AI coach from Proctorly. Ask about careers, AI, life choices, or just say hi. She speaks Hindi and Bhojpuri too."
              icon={<Bot className="w-5 h-5" />}
              cta="Start Chat"
              accent="yellow"
              testid="card-kiba"
              onClick={() => openBlitz("chat")}
              bullets={["Female AI voice", "Hindi · Bhojpuri · English", "Career & life advice"]}
            />
            <FeatureCard
              n="02"
              kicker="Solo · 10 MCQs"
              title={<>BLITZ.<br />Single player.</>}
              desc="Pick a topic. AI fires 10 multiple-choice questions. Answer by voice or tap. Beat the clock, earn your score, and walk away with a verifiable Proctorly certificate."
              icon={<Timer className="w-5 h-5" />}
              cta="Start Blitz"
              accent="yellow"
              testid="card-blitz"
              onClick={() => openBlitz()}
              bullets={["AI-generated MCQs", "Voice or tap answer", "Verifiable certificate"]}
            />
            <FeatureCard
              n="03"
              kicker="2 Players · 10 Questions"
              title={<>Head to head.<br />AI judges.</>}
              desc="Two players, ten questions each, one winner. AI scores every answer, the room watches the leaderboard climb, and confetti settles on the champion."
              icon={<Swords className="w-5 h-5" />}
              cta="Start Battle"
              accent="red"
              testid="card-battle"
              onClick={() => setBattleOpen(true)}
              bullets={["Live AI judging", "45-second turns", "Winner takes the gift"]}
            />
            <FeatureCard
              n="04"
              kicker="Mock Interview · AI Powered"
              title={<>Mock<br /><span className="text-[color:var(--kl-red)]">Interview.</span></>}
              desc="Practice a real technical interview with a live AI interviewer. Choose your role, get KPI-based scores, detailed feedback, and track your improvement across attempts."
              icon={<BriefcaseBusiness className="w-5 h-5" />}
              cta="View Open Roles"
              accent="red"
              testid="card-mock"
              onClick={() => navigate("/mock/positions")}
              bullets={["Live avatar interviewer", "Coding challenge round", "KPI score + verdict"]}
            />
          </div>
        </div>
      </section>

      {/* Live tournament: standalone link if one is active */}
      {tournament && (
        <section
          className="border-y border-white/10 py-7 px-6 md:px-12 cursor-pointer hover:bg-white/[0.03] transition"
          style={{ background: "linear-gradient(90deg, rgba(230,57,70,0.12), rgba(255,255,255,0.02))" }}
          data-testid="live-tournament-banner"
          onClick={() => navigate("/live")}
        >
          <div className="max-w-[1440px] mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <span className="pill-tag text-white/90"><span className="dot" />LIVE NOW</span>
              <div className="font-display text-2xl md:text-3xl uppercase text-white tracking-tight">
                {tournament.name} <span className="text-[color:var(--kl-red)]">·</span> Live Tournament
              </div>
            </div>
            <span className="btn-submit pointer-events-none">View Arena <ArrowUpRight className="w-4 h-4" /></span>
          </div>
        </section>
      )}


      {/* ===== Trust strip ===== */}
      <section className="border-y border-white/10 py-8 px-6 md:px-12" style={{ background: "rgba(255,255,255,0.02)" }}>
        <div className="max-w-[1440px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          <Strip icon={<Sparkles className="w-5 h-5" />} k="Powered by" v="Claude Sonnet 4.6" />
          <Strip icon={<ShieldCheck className="w-5 h-5" />} k="Verification" v="QR + Public Link" />
          <Strip icon={<Award className="w-5 h-5" />} k="Topics" v="GK · Bihar · Cinema · Science" />
          <Strip icon={<Swords className="w-5 h-5" />} k="Battle" v="2P · 10 Questions · AI Judge" />
        </div>
      </section>

      {/* ===== About / How it works ===== */}
      <section className="py-24 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="about-section">
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-12 items-start">
          <div>
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">02 — What is Proctorly</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-[0.9] mt-3 text-white">
              The fastest way to <span className="proctorly-mark">prove</span> what you actually know.
            </h2>
            <p className="text-white/60 mt-5 leading-relaxed">
              Proctorly is Kiba Labs' AI assessment engine. No question banks to leak. No essays to grade by hand. Every question is fresh, every answer is judged in seconds, and every certificate is publicly verifiable.
            </p>
          </div>

          <div className="space-y-5">
            <Step n="01" title="Pick your topic" desc="GK & Current Affairs · Know Your Bihar · Hindi Cinema · Science (Class 7–12). Four lanes, infinite questions." />
            <Step n="02" title="Answer the AI" desc="Speak or type. Claude Sonnet 4.6 generates the question and judges your response on the same rubric a panel would." />
            <Step n="03" title="Take home the proof" desc="A QR-verifiable certificate for Blitz wins. A confetti moment and a gift for Battle champions. LinkedIn-ready, always." />
          </div>
        </div>
      </section>

      {/* ===== Scan to Join (QR section) ===== */}
      <section className="border-y border-white/10 py-20 px-6 md:px-12" style={{ background: "rgba(255,255,255,0.06)" }} data-testid="qr-section">
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20">
          {/* Left: text + buttons */}
          <div className="flex-1 min-w-0">
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">03 — Scan & Join</div>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-[0.9] mt-3 text-white">
              At our booth?<br />
              <span className="text-[color:var(--kl-red)]">Scan and play.</span>
            </h2>
            <p className="text-white/60 mt-5 max-w-xl leading-relaxed">
              Print this QR. Stick it on table cards, booth banners, or the stage backdrop. Anyone who scans lands on a one-tap join form — no app, no install.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href="/join" className="btn-submit" data-testid="goto-join">
                Open Join Page <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => {
                  const canvas = document.getElementById("join-qr-canvas");
                  if (!canvas) return;
                  const link = document.createElement("a");
                  link.download = "proctorly-join-qr.png";
                  link.href = canvas.toDataURL("image/png");
                  link.click();
                }}
                className="btn-ghost-dark"
                data-testid="download-qr"
              >
                Download QR
              </button>
            </div>
          </div>
          {/* Right: QR code — dynamically generated, always points to /join */}
          <div className="shrink-0 flex flex-col items-center justify-center gap-3" data-testid="qr-card">
            <div className="rounded-2xl p-4 border border-white/15" style={{ background: "#fff" }}>
              <QRCodeCanvas
                id="join-qr-canvas"
                value={`${window.location.origin}/join`}
                size={240}
                bgColor="#ffffff"
                fgColor="#0d0f1a"
                level="H"
                imageSettings={{
                  src: "/kiba-k-logo.png",
                  x: undefined,
                  y: undefined,
                  height: 48,
                  width: 48,
                  excavate: true,
                }}
              />
            </div>
            <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40">proctorly.online/join</div>
          </div>
        </div>
      </section>

      {/* ===== AI Agents showcase ===== */}
      <section className="border-t border-white/10 py-20" style={{ background: "rgba(0,0,0,0.55)" }}>
        {/* Header — with side padding */}
        <div className="px-6 md:px-12 mb-10">
          <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">04 — Meet the Agents</div>
          <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tight leading-[0.9] mt-3 text-white">
            Your AI <span className="text-[color:var(--kl-red)]">Instructors.</span>
          </h2>
          <p className="text-white/55 mt-4 max-w-xl leading-relaxed">
            Five AI agents guide you through every challenge — from battle rules to career chats. Each one speaks English or Hindi. Muted here, but very much alive inside.
          </p>
        </div>

        {/* Full-width 5-column grid — no outer max-width constraint */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-0">
          {[
            { src: "/1.mp4" },
            { src: "/2.mp4" },
            { src: "/3.mp4" },
            { src: "/4.mp4" },
            { src: "/5.mp4" },
          ].map((agent, i) => (
            <AgentVideoCard key={agent.src} agent={agent} isLast={i === 4} idx={i} />
          ))}
        </div>
      </section>

      {/* ===== CTA strip ===== */}
      <section className="border-t border-white/10 px-6 md:px-12 py-16" style={{ background: "linear-gradient(135deg, rgba(230,57,70,0.08), rgba(230,57,70,0.02))" }}>
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)]">05 — Your move</div>
            <h3 className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] text-white mt-2">
              Sixty seconds is all we ask.
            </h3>
          </div>
          <button onClick={() => setBlitzOpen(true)} className="btn-submit text-base" data-testid="bottom-cta">
            Take the Blitz now <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/10 px-6 md:px-12 py-8 flex flex-wrap items-center justify-between gap-3 text-white/60">
        <div className="leading-tight">
          <div className="font-display text-2xl text-white">PROCTORLY <span className="text-[color:var(--kl-red)]">AI</span></div>
          <div className="font-mono-kl text-[9px] tracking-[0.25em] uppercase text-white/50 mt-0.5">Powered by Kiba Labs</div>
        </div>
        <div className="font-mono-kl text-[11px] uppercase tracking-widest">
          © {new Date().getFullYear()} · Built for the AI Summit
        </div>
      </footer>

      <RegistrationModal open={blitzOpen} onClose={() => setBlitzOpen(false)} onComplete={onBlitzDone} />
      <BattleRegistrationModal open={battleOpen} onClose={() => setBattleOpen(false)} />
    </div>
  );
}

function AgentVideoCard({ agent, isLast, idx }) {
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  // Don't even insert the <video> into the DOM until this slot's turn.
  // Each slot waits 550ms × its index, so the browser opens one decoder at a time.
  useEffect(() => {
    const tid = setTimeout(() => setReady(true), idx * 550);
    return () => clearTimeout(tid);
  }, [idx]);

  // Once the element exists, assign src imperatively and play.
  useEffect(() => {
    if (!ready) return;
    const el = videoRef.current;
    if (!el) return;
    const base = agent.src.split("?")[0];
    el.src = `${base}?cb=${idx}-${Date.now()}`;
    el.load();
    el.play().catch(() => {});
  }, [ready, agent.src, idx]);

  return (
    <div
      className="group relative overflow-hidden"
      style={{
        borderRight: isLast ? "none" : "1px solid rgba(230,57,70,0.15)",
        background: "#000",
        aspectRatio: "3/4",
      }}
    >
      {ready && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700"
        />
      )}
      {/* bottom gradient */}
      <div
        className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.90), transparent)" }}
      />
      {/* badge */}
      <div className="absolute bottom-3 inset-x-4 flex items-end justify-between">
        <div className="font-display text-xl uppercase text-white leading-tight">AI Agent</div>
        <span
          className="px-2 py-0.5 rounded-full font-mono-kl text-[9px] uppercase tracking-widest"
          style={{ background: "rgba(230,57,70,0.25)", color: "var(--kl-red)", border: "1px solid rgba(230,57,70,0.5)" }}
        >
          AI
        </span>
      </div>
    </div>
  );
}

function FeatureCard({ n, kicker, title, desc, icon, cta, onClick, accent, testid, bullets }) {
  const colors = {
    red:    { bg: "rgba(230,57,70,0.18)", line: "rgba(230,57,70,0.6)",  text: "var(--kl-red)", dot: "bg-[color:var(--kl-red)]" },
    yellow: { bg: "rgba(230,57,70,0.06)", line: "rgba(230,57,70,0.35)", text: "var(--kl-red)", dot: "bg-[color:var(--kl-red)]" },
  };
  const c = colors[accent] || colors.yellow;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group feature-card text-left rounded-lg border border-white/10 p-7 md:p-8 flex flex-col gap-6 min-h-[440px] relative overflow-hidden"
      style={{
        background: `linear-gradient(165deg, ${c.bg}, rgba(255,255,255,0.02) 55%)`,
        backdropFilter: "blur(8px)",
        borderColor: c.line.replace("0.6", "0.35").replace("0.5", "0.35"),
      }}
    >
      {/* corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-40 pointer-events-none" style={{ background: `radial-gradient(circle at top right, ${c.line}, transparent 70%)` }} />

      <div className="flex items-center justify-between relative z-10">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-mono-kl border" style={{ borderColor: c.line, background: c.bg, color: c.text }}>
          {icon}
          {kicker}
        </span>
        <span className="font-mono-kl text-xs text-white/30 uppercase tracking-widest">{n}</span>
      </div>

      <div className="relative z-10">
        <div className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.9] tracking-tight text-white">{title}</div>
      </div>

      <p className="text-white/65 text-sm md:text-base leading-relaxed flex-1 relative z-10">{desc}</p>

      <div className="space-y-2 relative z-10">
        {bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-white/70">
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
            {b}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between relative z-10 pt-2">
        <span className="btn-submit pointer-events-none">
          {cta} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
        </span>
        <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30">Click anywhere</span>
      </div>
    </button>
  );
}

function Strip({ icon, k, v }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-[color:var(--kl-red)]" style={{ background: "rgba(230,57,70,0.1)", border: "1px solid rgba(230,57,70,0.3)" }}>
        {icon}
      </div>
      <div>
        <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40">{k}</div>
        <div className="text-white text-sm md:text-base font-semibold mt-0.5">{v}</div>
      </div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="rounded-md border border-white/10 p-6 hover:border-[color:var(--kl-red)]/50 transition-colors" style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="flex items-baseline gap-4">
        <span className="font-display text-3xl text-[color:var(--kl-red)]">{n}</span>
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-white">{title}</div>
          <p className="text-white/60 mt-2 leading-relaxed">{desc}</p>
        </div>
      </div>
    </div>
  );
}
